import type { EpisodeApiResponse, SearchResult, LiveStatus, RecentEpisode, NextEpisodeResponse, ScheduleResponse, StationShow, IcecastMetadata } from '../types';

const API_BASE = '';

class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    throw new ApiError(response.status, `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetch episode data by ID
 */
export async function getEpisode(episodeId: number): Promise<EpisodeApiResponse> {
  return fetchJson<EpisodeApiResponse>(`${API_BASE}/api/episode/${episodeId}`);
}

/**
 * Fetch station/stream data
 */
export async function getStation(streamId: number): Promise<EpisodeApiResponse> {
  return fetchJson<EpisodeApiResponse>(`${API_BASE}/api/station/${streamId}`);
}

/**
 * Fetch live station data
 */
export async function getLiveStation(streamId: number): Promise<EpisodeApiResponse & { live: true; current?: { title: string } }> {
  return fetchJson(`${API_BASE}/api/station/${streamId}/live`);
}

/**
 * Fetch station by slug (rss filename)
 */
export async function getStationBySlug(slug: string): Promise<{ stream: EpisodeApiResponse['stream'] }> {
  return fetchJson(`${API_BASE}/api/station/by-slug/${slug}`);
}

/**
 * Search episodes
 */
export async function searchEpisodes(
  streamId: number,
  searchTerm: string
): Promise<SearchResult[]> {
  const response = await fetch(`${API_BASE}/search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      stationid: streamId,
      search: searchTerm,
    }),
  });

  if (!response.ok) {
    throw new ApiError(response.status, 'Search failed');
  }

  return response.json();
}

/**
 * Get now playing info for live stream
 */
export async function getNowPlaying(streamId: number): Promise<LiveStatus> {
  return fetchJson<LiveStatus>(`${API_BASE}/api/station/${streamId}/now-playing`);
}

/**
 * Find the episode that was airing at a given Unix timestamp.
 * Returns the episode ID and calculated seek position.
 */
export async function findEpisodeByTimestamp(
  streamId: number,
  timestamp: number,
  offset: number = 0
): Promise<{ episodeid: number; seek: number; episode_start: number; episode_duration: number }> {
  return fetchJson(
    `${API_BASE}/api/station/${streamId}/find-episode?timestamp=${timestamp}&offset=${offset}`
  );
}

/**
 * Get recent episodes from a station
 */
export async function getRecentStationEpisodes(
  streamId: number,
  limit: number = 10
): Promise<RecentEpisode[]> {
  return fetchJson<RecentEpisode[]>(
    `${API_BASE}/api/station/${streamId}/recent?limit=${limit}`
  );
}

/**
 * Get recent episodes from a specific show
 */
export async function getRecentShowEpisodes(
  showId: number,
  limit: number = 10
): Promise<RecentEpisode[]> {
  return fetchJson<RecentEpisode[]>(
    `${API_BASE}/api/show/${showId}/recent?limit=${limit}`
  );
}

/**
 * Get the next episode for autoplay
 * Returns next from same show first, then from station
 */
export async function getNextEpisode(episodeId: number): Promise<NextEpisodeResponse> {
  return fetchJson<NextEpisodeResponse>(
    `${API_BASE}/api/episode/${episodeId}/next`
  );
}

/**
 * Get station's daily schedule
 */
export async function getStationSchedule(streamId: number): Promise<ScheduleResponse> {
  return fetchJson<ScheduleResponse>(
    `${API_BASE}/api/station/${streamId}/schedule`
  );
}

/**
 * Get all published shows for a station
 */
export async function getStationShows(streamId: number): Promise<StationShow[]> {
  return fetchJson<StationShow[]>(`${API_BASE}/api/station/${streamId}/shows`);
}

/**
 * Get current track from Icecast metadata
 */
export async function getIcecastMetadata(streamId: number): Promise<IcecastMetadata> {
  return fetchJson<IcecastMetadata>(`${API_BASE}/api/station/${streamId}/icecast-metadata`);
}

export { ApiError };
